import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Chip, ToggleButton, ToggleButtonGroup,
  FormControl, Select, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getAllSales, getAllSalesByAgentId, getAllSalesByGroupName } from '../../api/salesApi';
import { getAllAgentGroups } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM, formatCompactRm } from '../../utils/priceUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";
const CATEGORY_COLORS = { RESIDENTIAL: '#1A8CFF', COMMERCIAL: '#F59E0B' };
const PIE_COLORS = ['#1A8CFF', '#F59E0B', '#10B981', '#6366F1', '#EF4444', '#8B5CF6'];

const DATE_PRESETS = [
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
  { label: 'All Time', start: () => null, end: () => null },
];

function handleAgentName(first, last, alias) {
  const hasFirst = first && first.trim();
  const hasLast = last && last.trim();
  if (hasFirst && hasLast) return `${last} ${first}`;
  if (hasFirst) return first;
  if (hasLast) return last;
  return alias || 'Unknown';
}

export default function SalesReport() {
  const { user } = useUser();
  const navigate = useNavigate();

  // Tab availability is role-driven (NOT right-driven) so the matrix is:
  //   Sales Agent / In-house Agent   → Personal
  //   Leader                          → Personal · Group
  //   Accountant / Super Admin        → Personal · Group (picker) · All
  //   Group Admin / Admin / Astern    → no access (route gate blocks entry)
  const roles = user?.roles || [];
  const isAccountantOrSuper = roles.includes('Accountant') || roles.includes('Super Admin');
  const hasAny = (list) => roles.some(r => list.includes(r));
  const availableTabs = useMemo(() => {
    const t = [];
    if (hasAny(['Sales Agent', 'In-house Agent', 'Leader', 'Accountant', 'Super Admin'])) t.push({ key: 'personal', label: 'Personal Sales' });
    if (hasAny(['Leader', 'Accountant', 'Super Admin']))                                  t.push({ key: 'group',    label: 'Group Sales' });
    if (hasAny(['Accountant', 'Super Admin']))                                            t.push({ key: 'all',      label: 'All Sales' });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [activeTab, setActiveTab] = useState(availableTabs[0]?.key || '');
  useEffect(() => {
    if (!activeTab && availableTabs.length > 0) setActiveTab(availableTabs[0].key);
  }, [availableTabs, activeTab]);

  // For Accountant/Super Admin on the Group tab — group picker.
  const [groups, setGroups] = useState([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  useEffect(() => {
    if (!isAccountantOrSuper) return;
    let cancelled = false;
    getAllAgentGroups().then(r => {
      if (cancelled) return;
      const list = r.data || [];
      setGroups(list);
      if (!selectedGroupName && list.length > 0) setSelectedGroupName(list[0].groupName || list[0].name || '');
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountantOrSuper]);

  // Group name to query against — leader's own group, or the picked one.
  const groupNameForQuery = isAccountantOrSuper ? selectedGroupName : (user?.groupName || '');

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [activePreset, setActivePreset] = useState('This Month');
  const [stateMetric, setStateMetric] = useState('revenue');
  const [cityMetric, setCityMetric] = useState('revenue');
  const [cityStateFilter, setCityStateFilter] = useState('');

  const fetchData = useCallback(async () => {
    if (!activeTab) { setLoading(false); return; }
    setLoading(true);
    try {
      let res;
      if (activeTab === 'all') res = await getAllSales();
      else if (activeTab === 'group') {
        if (!groupNameForQuery) { setSales([]); setLoading(false); return; }
        res = await getAllSalesByGroupName(groupNameForQuery);
      } else {
        res = await getAllSalesByAgentId(user?.userId);
      }
      setSales(res.data || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, groupNameForQuery, user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    setStartDate(preset.start());
    setEndDate(preset.end());
  };

  const handleStartDateChange = (val) => {
    if (val) { setStartDate(val); setActivePreset(null); }
  };

  const handleEndDateChange = (val) => {
    if (val) { setEndDate(val); setActivePreset(null); }
  };

  /* ---------- Filtered data ---------- */
  const filteredSales = useMemo(() => {
    if (!startDate && !endDate) return sales; // All Time
    return sales.filter(s => {
      if (!s.depositOn) return false;
      const date = dayjs(s.depositOn);
      if (startDate && date.isBefore(startDate, 'day')) return false;
      if (endDate && date.isAfter(endDate, 'day')) return false;
      return true;
    });
  }, [sales, startDate, endDate]);

  /* ---------- Summary metrics ---------- */
  const totalSalesValue = useMemo(() =>
    filteredSales.reduce((sum, s) => sum + (s.finalPrice ?? 0), 0), [filteredSales]);

  const numberOfSales = filteredSales.length;

  const averageSaleValue = numberOfSales > 0 ? totalSalesValue / numberOfSales : 0;

  const categoryBreakdown = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const cat = s.productCategory || 'Unknown';
      map[cat] = (map[cat] ?? 0) + (s.finalPrice ?? 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredSales]);

  const topCategory = categoryBreakdown.length > 0 ? categoryBreakdown[0].name : '-';

  /* ---------- Charts data ---------- */
  const salesByMonth = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const month = dayjs(s.depositOn).format('MMM YYYY');
      map[month] = (map[month] ?? 0) + (s.finalPrice ?? 0);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [filteredSales]);

  const salesByGroup = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const group = s.agentDetails?.groupName ?? 'Unknown';
      map[group] = (map[group] ?? 0) + (s.finalPrice ?? 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredSales]);

  const salesByState = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const state = s.customerDetails?.state || s.customer?.state;
      if (!state) return;
      if (!map[state]) map[state] = { name: state, revenue: 0, count: 0 };
      map[state].revenue += s.finalPrice ?? 0;
      map[state].count += 1;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filteredSales]);

  const salesByCity = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const state = s.customerDetails?.state || s.customer?.state;
      const city = s.customerDetails?.city || s.customer?.city;
      if (!city) return;
      const key = `${city}, ${state || '—'}`;
      if (!map[key]) map[key] = { name: key, state, city, revenue: 0, count: 0 };
      map[key].revenue += s.finalPrice ?? 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filteredSales]);

  const salesByKwpPackage = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const kwp = Number(s.energy);
      if (!kwp || kwp <= 0) return;
      const key = `${kwp.toFixed(2)} kWp`;
      if (!map[key]) map[key] = { name: key, kwp, count: 0 };
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => a.kwp - b.kwp);
  }, [filteredSales]);

  const topAgents = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const hasCobroke = !!s.coAgentDetails;
      const countShare = hasCobroke ? 0.5 : 1;
      const valueShare = hasCobroke ? (s.finalPrice ?? 0) / 2 : (s.finalPrice ?? 0);

      // Main agent
      const id = s.agentDetails?.userId ?? s.agentId ?? 'unknown';
      if (!map[id]) {
        const d = s.agentDetails;
        map[id] = { agentId: id, name: d ? handleAgentName(d.firstName, d.lastName, d.alias) : 'Unknown', group: d?.groupName ?? '-', total: 0, count: 0 };
      }
      map[id].total += valueShare;
      map[id].count += countShare;

      // Co-agent
      if (hasCobroke) {
        const coId = s.coAgentDetails.userId ?? 'unknown-co';
        if (!map[coId]) {
          const cd = s.coAgentDetails;
          map[coId] = { agentId: coId, name: handleAgentName(cd.firstName, cd.lastName, cd.alias), group: cd.groupName ?? '-', total: 0, count: 0 };
        }
        map[coId].total += valueShare;
        map[coId].count += countShare;
      }
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [filteredSales]);

  const teamBreakdown = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const hasCobroke = !!s.coAgentDetails;
      const countShare = hasCobroke ? 0.5 : 1;
      const valueShare = hasCobroke ? (s.finalPrice ?? 0) / 2 : (s.finalPrice ?? 0);

      const mainGroup = s.agentDetails?.groupName ?? '-';
      if (!map[mainGroup]) map[mainGroup] = { group: mainGroup, total: 0, count: 0, agents: new Set() };
      map[mainGroup].total += valueShare;
      map[mainGroup].count += countShare;
      if (s.agentDetails?.userId) map[mainGroup].agents.add(s.agentDetails.userId);

      if (hasCobroke) {
        const coGroup = s.coAgentDetails?.groupName ?? '-';
        if (!map[coGroup]) map[coGroup] = { group: coGroup, total: 0, count: 0, agents: new Set() };
        map[coGroup].total += valueShare;
        map[coGroup].count += countShare;
        if (s.coAgentDetails?.userId) map[coGroup].agents.add(s.coAgentDetails.userId);
      }
    });
    return Object.values(map)
      .map(t => ({ ...t, agents: t.agents.size }))
      .sort((a, b) => b.total - a.total);
  }, [filteredSales]);

  /* ---------- Summary cards config ---------- */
  const summaryCards = [
    { label: 'Total Sales', value: handlePriceWithRM(totalSalesValue), sub: `${numberOfSales} sales this period`, borderColor: '#1A8CFF' },
    { label: 'Number of Sales', value: numberOfSales, sub: 'Completed transactions', borderColor: '#F59E0B' },
    { label: 'Average Sale Value', value: handlePriceWithRM(Math.round(averageSaleValue)), sub: 'Per transaction', borderColor: '#10B981' },
    { label: 'Top Category', value: topCategory, sub: topCategory !== '-' ? `${handlePriceWithRM(categoryBreakdown[0]?.value ?? 0)}` : 'No data', borderColor: '#6366F1' },
  ];

  /* ---------- Styles ---------- */
  const datePickerSx = {
    width: 150,
    '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' },
  };

  if (availableTabs.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Sales Report</h2>
        <p style={{ marginTop: 8, fontSize: '0.85rem', color: '#64748B', fontFamily: FONT }}>
          Your role does not have access to any Sales Report view.
        </p>
      </Box>
    );
  }

  if (loading) return <div>Loading...</div>;

  const scopeSubtitle = activeTab === 'all'
    ? 'All sales performance overview'
    : activeTab === 'group'
    ? `Group sales performance overview${groupNameForQuery ? ` — ${groupNameForQuery}` : ''}`
    : 'Your personal sales performance overview';

  return (
    <div>
      {/* ===== Header ===== */}
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
          Print / Save as PDF
        </Button>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Sales Report</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>{scopeSubtitle}</p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }}
          />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }}
          />
        </Box>
      </Box>

      {/* ===== Scope Tabs (Personal / Group / All) — only shown if user has >1 scope ===== */}
      {availableTabs.length > 1 && (
        <Box className="no-print" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive value={activeTab} onChange={(_, v) => v && setActiveTab(v)} size="small"
            sx={{ '& .MuiToggleButton-root': { fontFamily: FONT, fontSize: '0.78rem', textTransform: 'none', py: 0.6, px: 1.5 },
                  '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
            {availableTabs.map(t => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
          </ToggleButtonGroup>
          {activeTab === 'group' && isAccountantOrSuper && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={selectedGroupName} onChange={(e) => setSelectedGroupName(e.target.value)}
                displayEmpty sx={{ fontFamily: FONT, fontSize: '0.82rem', borderRadius: '8px' }}>
                {groups.length === 0 && <MenuItem value="" disabled>No groups</MenuItem>}
                {groups.map(g => {
                  const name = g.groupName || g.name || '';
                  return <MenuItem key={name} value={name} sx={{ fontFamily: FONT, fontSize: '0.82rem' }}>{name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {/* ===== Date Presets ===== */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(preset => (
          <Chip
            key={preset.label}
            label={preset.label}
            size="small"
            onClick={() => handlePreset(preset)}
            sx={{
              fontFamily: FONT,
              fontSize: '0.75rem',
              fontWeight: activePreset === preset.label ? 600 : 400,
              backgroundColor: activePreset === preset.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === preset.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === preset.label ? '#1574D4' : '#E2E8F0' },
            }}
          />
        ))}
      </Box>

      {/* ===== Summary Cards ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {summaryCards.map(card => (
          <Card key={card.label} sx={{ borderLeft: `4px solid ${card.borderColor}`, borderRadius: '14px' }}>
            <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>
                {card.value}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>
                {card.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ===== Charts Row ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
        {/* Sales by Month */}
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Sales by Month
            </Typography>
            {salesByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={salesByMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis tickFormatter={v => formatCompactRm(v)} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip formatter={value => handlePriceWithRM(value)} labelStyle={{ fontFamily: FONT, fontSize: '0.8rem' }} />
                  <Bar dataKey="value" fill="#1A8CFF" radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
            )}
          </CardContent>
        </Card>

        {/* Sales by Category (Donut) */}
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Sales by Category
            </Typography>
            {categoryBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={categoryBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={85}
                    dataKey="value"
                    label={({ name, value, x, y, textAnchor }) => (
                      <text x={x} y={y} fill="#475569" textAnchor={textAnchor} dominantBaseline="central"
                        style={{ fontSize: 10, fontFamily: FONT, fontWeight: 500 }}>
                        {`${name}: ${formatCompactRm(value)}`}
                      </text>
                    )}
                    labelLine={false}
                  >
                    {categoryBreakdown.map((entry, i) => (
                      <Cell key={entry.name} fill={CATEGORY_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={value => handlePriceWithRM(value)} />
                  <Legend formatter={value => (
                    <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>
                  )} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Sales by State + Sales by City ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', fontFamily: FONT }}>Sales by State</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: FONT }}>{salesByState.length} states · sorted by {stateMetric}</Typography>
              </Box>
              <ToggleButtonGroup size="small" exclusive value={stateMetric} onChange={(_, v) => v && setStateMetric(v)}
                sx={{ '& .MuiToggleButton-root': { fontFamily: FONT, fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' } }}>
                <ToggleButton value="revenue">Revenue</ToggleButton>
                <ToggleButton value="count">Cases</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {salesByState.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(220, salesByState.length * 28)}>
                <BarChart data={[...salesByState].sort((a, b) => b[stateMetric] - a[stateMetric])} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" allowDecimals={false}
                    tickFormatter={v => stateMetric === 'revenue' ? formatCompactRm(v) : v}
                    style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis type="category" dataKey="name" width={120} style={{ fontSize: '0.72rem', fontFamily: FONT }} />
                  <Tooltip formatter={value => stateMetric === 'revenue' ? handlePriceWithRM(value) : `${value} cases`} />
                  <Bar dataKey={stateMetric} fill="#6366F1" radius={[0, 4, 4, 0]} barSize={18}
                    label={{ position: 'right', fill: '#0F172A', fontSize: 11, fontFamily: FONT, formatter: v => stateMetric === 'revenue' ? formatCompactRm(v) : v }} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No sales with state info yet</Typography>}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', fontFamily: FONT }}>Sales by City</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: FONT }}>Top 15 · sorted by {cityMetric}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select value={cityStateFilter} onChange={e => setCityStateFilter(e.target.value)} displayEmpty
                    sx={{ fontFamily: FONT, fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.5 } }}>
                    <MenuItem value=""><em>All states</em></MenuItem>
                    {salesByState.map(s => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <ToggleButtonGroup size="small" exclusive value={cityMetric} onChange={(_, v) => v && setCityMetric(v)}
                  sx={{ '& .MuiToggleButton-root': { fontFamily: FONT, fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' } }}>
                  <ToggleButton value="revenue">Revenue</ToggleButton>
                  <ToggleButton value="count">Cases</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            {(() => {
              const filtered = cityStateFilter ? salesByCity.filter(c => c.state === cityStateFilter) : salesByCity;
              const top = [...filtered].sort((a, b) => b[cityMetric] - a[cityMetric]).slice(0, 15);
              return top.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(220, top.length * 26)}>
                  <BarChart data={top} layout="vertical" margin={{ left: 10, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" allowDecimals={false}
                      tickFormatter={v => cityMetric === 'revenue' ? formatCompactRm(v) : v}
                      style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                    <YAxis type="category" dataKey="name" width={140} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                    <Tooltip formatter={value => cityMetric === 'revenue' ? handlePriceWithRM(value) : `${value} cases`} />
                    <Bar dataKey={cityMetric} fill="#F59E0B" radius={[0, 4, 4, 0]} barSize={16}
                      label={{ position: 'right', fill: '#0F172A', fontSize: 11, fontFamily: FONT, formatter: v => cityMetric === 'revenue' ? formatCompactRm(v) : v }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No sales with city info yet</Typography>;
            })()}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Sales by kWp Package ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>
              Sales by kWp Package
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
              Number of cases per package size in selected date range
            </Typography>
            {salesByKwpPackage.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(220, salesByKwpPackage.length * 28)}>
                <BarChart data={salesByKwpPackage} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis type="category" dataKey="name" width={80} style={{ fontSize: '0.72rem', fontFamily: FONT }} />
                  <Tooltip formatter={value => [`${value} cases`, 'Count']} labelStyle={{ fontFamily: FONT, fontSize: '0.8rem' }} />
                  <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} barSize={18} label={{ position: 'right', fill: '#0F172A', fontSize: 11, fontFamily: FONT }} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Cross-group sections — only on the "All Sales" tab ===== */}
      {activeTab === 'all' && (
        <>
          {/* Sales by Group */}
          <Box sx={{ mb: 2 }}>
            <Card sx={{ borderRadius: '14px' }}>
              <CardContent>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
                  Sales by Group
                </Typography>
                {salesByGroup.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(200, salesByGroup.length * 40)}>
                    <BarChart data={salesByGroup} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tickFormatter={v => formatCompactRm(v)} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                      <YAxis type="category" dataKey="name" width={100} style={{ fontSize: '0.75rem', fontFamily: FONT }} />
                      <Tooltip formatter={value => handlePriceWithRM(value)} />
                      <Bar dataKey="value" fill="#6366F1" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Top 10 Agents Table */}
          <Card sx={{ borderRadius: '14px' }}>
            <CardContent>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
                Top 10 Agents
              </Typography>
              {topAgents.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0' } }}>
                        <TableCell>#</TableCell>
                        <TableCell>Agent</TableCell>
                        <TableCell>Group</TableCell>
                        <TableCell align="right">Sales Count</TableCell>
                        <TableCell align="right">Total Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topAgents.map((agent, i) => (
                        <TableRow key={agent.agentId} hover
                          onClick={() => agent.agentId && agent.agentId !== 'unknown' && navigate(`/reports/commission/agent/${agent.agentId}`)}
                          sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }, '&:hover': { backgroundColor: '#F0F7FF' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#64748B' }}>{i + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#1A8CFF' }}>{agent.name}</TableCell>
                          <TableCell>{agent.group}</TableCell>
                          <TableCell align="right">{agent.count}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{handlePriceWithRM(agent.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
              )}
            </CardContent>
          </Card>

          {/* Team Breakdown */}
          <Card sx={{ borderRadius: '14px', mt: 3 }}>
            <CardContent>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
                Team Breakdown
              </Typography>
              {teamBreakdown.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0' } }}>
                        <TableCell>#</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell align="right">Active Agents</TableCell>
                        <TableCell align="right">Sales Count</TableCell>
                        <TableCell align="right">Total Value</TableCell>
                        <TableCell align="right">Avg Per Sale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamBreakdown.map((team, i) => (
                        <TableRow key={team.group}
                          sx={{ '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#64748B' }}>{i + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{team.group}</TableCell>
                          <TableCell align="right">{team.agents}</TableCell>
                          <TableCell align="right">{team.count}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{handlePriceWithRM(team.total)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(team.count > 0 ? Math.round(team.total / team.count) : 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
